import { Language } from '../types';

// Map our language codes to Speech API language codes
const SPEECH_LANG_MAP: Record<Language, string> = {
  en: 'en-US',
  es: 'es-ES',
  fr: 'fr-FR',
  de: 'de-DE',
  it: 'it-IT',
  pt: 'pt-PT',
  nl: 'nl-NL',
  pl: 'pl-PL',
};

class SpeechService {
  private synthesis: SpeechSynthesis | null = null;
  private voices: SpeechSynthesisVoice[] = [];
  private isInitialized = false;

  constructor() {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      this.synthesis = window.speechSynthesis;
      this.loadVoices();

      // Voices are loaded asynchronously in some browsers
      if (this.synthesis.onvoiceschanged !== undefined) {
        this.synthesis.onvoiceschanged = () => {
          this.loadVoices();
        };
      }
    }
  }

  private loadVoices(): void {
    if (this.synthesis) {
      this.voices = this.synthesis.getVoices();
      this.isInitialized = true;
    }
  }

  private getVoiceForLanguage(language: Language): SpeechSynthesisVoice | null {
    const langCode = SPEECH_LANG_MAP[language];

    // Try to find a voice that matches the language code
    const matchingVoice = this.voices.find((voice) =>
      voice.lang.startsWith(langCode.split('-')[0])
    );

    return matchingVoice || this.voices[0] || null;
  }

  /**
   * Speak a word using the browser's Speech Synthesis API
   */
  speak(text: string, language: Language, options?: {
    rate?: number;
    pitch?: number;
    volume?: number;
    onEnd?: () => void;
    onStart?: () => void;
  }): void {
    if (!this.synthesis) {
      console.warn('Speech synthesis not supported in this browser');
      options?.onEnd?.();
      return;
    }

    // iOS Safari workaround: Reload voices if empty (voices may not be ready on first load)
    if (this.voices.length === 0) {
      this.voices = this.synthesis.getVoices();
      console.log('Reloaded voices for iOS, count:', this.voices.length);
    }

    // Only cancel if actually speaking (iOS Safari workaround)
    // Calling cancel() when nothing is playing can cause issues on iOS
    if (this.synthesis.speaking) {
      this.synthesis.cancel();
    }

    // Create and configure the utterance
    const utterance = new SpeechSynthesisUtterance(text);
    const voice = this.getVoiceForLanguage(language);

    if (voice) {
      utterance.voice = voice;
      console.log('Using voice:', voice.name, voice.lang);
    } else {
      console.warn('No voice found for language:', language, 'Available voices:', this.voices.length);
    }

    utterance.lang = SPEECH_LANG_MAP[language];
    utterance.rate = options?.rate ?? 0.9; // Slightly slower for kids
    utterance.pitch = options?.pitch ?? 1.0;
    utterance.volume = options?.volume ?? 1.0;

    if (options?.onEnd) {
      utterance.onend = options.onEnd;
    }

    if (options?.onStart) {
      utterance.onstart = options.onStart;
    }

    utterance.onerror = (event) => {
      console.error('Speech synthesis error:', event);
      console.error('Error details:', {
        error: event.error,
        charIndex: event.charIndex,
        charLength: event.charLength,
      });
      options?.onEnd?.();
    };

    console.log('Speaking text:', text, 'in language:', language);

    // Speak the utterance (must be synchronous for iOS user interaction requirement)
    this.synthesis.speak(utterance);
  }

  /**
   * Stop any ongoing speech
   */
  stop(): void {
    if (this.synthesis) {
      this.synthesis.cancel();
    }
  }

  /**
   * Check if speech synthesis is supported and ready
   */
  isSupported(): boolean {
    return this.synthesis !== null && this.isInitialized;
  }

  /**
   * Get available languages
   */
  getAvailableLanguages(): Language[] {
    const availableLangs = new Set<Language>();

    this.voices.forEach((voice) => {
      const lang = voice.lang.toLowerCase();
      Object.entries(SPEECH_LANG_MAP).forEach(([key, value]) => {
        if (lang.startsWith(value.toLowerCase().split('-')[0])) {
          availableLangs.add(key as Language);
        }
      });
    });

    return Array.from(availableLangs);
  }
}

// Export a singleton instance
export const speechService = new SpeechService();
